import { ImageAnalysisSlideJobStatus } from 'api/jobs';
import { additionalNavigationParamsSchema } from 'components/Procedure/Header/NavigationPanel/additionalNavigationParamsSchema';
import { ResultReviewIntent, resultReviewIntentParam } from 'components/Procedure/resultReviewIntent';
import { ResultsMode, ResultType } from 'interfaces/experimentResults';
import { JobStatus, JobType } from 'interfaces/job';
import { Procedure } from 'interfaces/procedure';
import { compact, filter, includes, map } from 'lodash';
import { DelimitedArrayParam } from 'use-query-params';
import { filtersQueryParamsSchema } from 'utils/useEncodedFilters/constants';
import { encodeQueryParamsUsingSchema } from 'utils/useEncodedFilters/helpers';

interface CellSegmentationReviewUrlProps {
  studyId: string;
  labId: string;
  procedures: Procedure[];
  statuses: ImageAnalysisSlideJobStatus[];
}

const getReviewIntent = (resultType: ResultType): ResultReviewIntent => ({
  version: 1,
  selector: {
    resultTypes: [resultType],
  },
  selection: 'latest_per_slide',
  view: 'overlays',
  activate: 'all',
});

export const getWorkflowJobReviewUrl = ({
  studyId,
  labId,
  procedures,
  statuses,
  jobType,
  resultType,
}: CellSegmentationReviewUrlProps & { jobType: JobType; resultType: ResultType }) => {
  const completedSlideIds = map(
    filter(statuses, (status) => status.jobType === jobType && status.status === JobStatus.Completed),
    'slideId'
  );
  const matchingCases = compact(
    map(procedures, (procedure) => {
      const slideIds = filter(
        map(procedure.slides || [], 'id'),
        (slideId) => Boolean(slideId) && includes(completedSlideIds, slideId)
      );

      return slideIds.length ? { caseId: procedure.id, slideIds } : null;
    })
  );
  const firstCase = matchingCases[0];
  const firstSlideId = firstCase?.slideIds[0];

  if (!firstCase || !firstSlideId) {
    return undefined;
  }

  const encodedParams = encodeQueryParamsUsingSchema(
    {
      filters: { studyId },
      labId,
      resultsMode: ResultsMode.All,
      datasetJobFilters: {
        studyId,
        types: [jobType],
        statuses: [JobStatus.Completed],
        childTasksOnly: true,
        attemptSelection: 'latest_per_slide',
      },
      datasetJobFiltersIncludeExclude: 'include',
      selectedSlideIds: [firstSlideId],
      viewersStainPreferences: ['mplex'],
      'slideInfobarTab-0': 'overlays',
      [resultReviewIntentParam]: getReviewIntent(resultType),
    },
    {
      ...filtersQueryParamsSchema,
      ...additionalNavigationParamsSchema,
      selectedSlideIds: DelimitedArrayParam,
    },
    { arrayFormat: 'repeat' }
  );

  return {
    pathname: `/procedures/${firstCase.caseId}`,
    search: `?${encodedParams}`,
  };
};

export const getCellSegmentationReviewUrl = (props: CellSegmentationReviewUrlProps) =>
  getWorkflowJobReviewUrl({
    ...props,
    jobType: JobType.MultiplexCellSegmentation,
    resultType: ResultType.SlideInferenceCellSegmentation,
  });

export const getRnaSpotDetectionReviewUrl = (props: CellSegmentationReviewUrlProps) =>
  getWorkflowJobReviewUrl({
    ...props,
    jobType: JobType.MultiplexPeakDetection,
    resultType: ResultType.SlideDetectionsTable,
  });
